import { Check, Truck } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToId } from "@/lib/scrollTo";

const TIERS = [
  {
    id: "9x12",
    name: "The Keepsake",
    size: '9" × 12"',
    price: 100,
    features: ["Hand-painted original, signed", "Your choice of background color", "One crisp headshot + candid references", "Protective studio packaging"],
    featured: false,
  },
  {
    id: "14x18",
    name: "The Portrait",
    size: '14" × 18"',
    price: 175,
    features: ["Hand-painted original, signed & dated", "Background color + 1 accent color", "Gallery presence for any room", "Progress update from the easel"],
    featured: true,
  },
  {
    id: "26x30",
    name: "The Masterpiece",
    size: '26" × 30"',
    price: 350,
    features: ["Statement-scale signed original", "Background color + 2 accent colors", "Free shipping, fully insured", "The centerpiece of your home"],
    featured: false,
    shipping: true,
  },
];

export const Pricing = ({ onSelect }) => {
  const choose = (id) => {
    onSelect(id);
    scrollToId("consult");
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-28 sm:py-36 bg-[#100D0A] overflow-hidden">
      <div className="absolute inset-0 spotlight" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal><Eyebrow className="text-center block">Commission Tiers</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 text-center font-serif tracking-tight leading-[1.08] text-3xl sm:text-4xl lg:text-5xl text-[#EAF5FA]">
            One price. <span className="gold-foil-text italic">One original.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-5 text-center text-sm sm:text-base text-stone-400 max-w-xl mx-auto">
            No subscriptions, no print runs, no surprises — just the size that fits your wall.
          </p>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-3 gap-6 items-stretch">
          {TIERS.map((t, i) => (
            <Reveal key={t.id} delay={0.15 + i * 0.12} className="h-full">
              <div
                data-testid={`pricing-card-${t.id}`}
                className={`relative h-full flex flex-col p-8 rounded-2xl border transition-colors duration-500 ${
                  t.featured
                    ? "bg-[#1F1B17] border-[#41C2F2]/70 shadow-[0_0_60px_-15px_rgba(65,194,242,0.4)]"
                    : "bg-[#181512] border-[#2E2721] hover:border-[#41C2F2]/40"
                }`}
              >
                {t.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#41C2F2] text-[#0C0A09] text-[11px] font-semibold tracking-[0.15em] uppercase">
                    Most Loved
                  </span>
                )}
                <p className="text-xs uppercase tracking-[0.25em] font-mono text-stone-500">{t.name}</p>
                <p className="mt-4 font-serif text-3xl text-[#EAF5FA]">{t.size}</p>
                <p className="mt-2 flex items-baseline gap-1">
                  <span className="font-serif text-5xl gold-foil-text">${t.price}</span>
                </p>
                <ul className="mt-7 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-stone-300">
                      <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#41C2F2]" />
                      {f}
                    </li>
                  ))}
                </ul>
                {t.shipping && (
                  <p className="mt-5 inline-flex items-center gap-2 text-xs text-[#41C2F2] font-medium">
                    <Truck className="w-4 h-4" /> Free shipping included
                  </p>
                )}
                <button
                  data-testid={`pricing-select-btn-${t.id}`}
                  onClick={() => choose(t.id)}
                  className={`mt-8 w-full py-3.5 rounded-full text-sm font-semibold tracking-wide transition-colors duration-300 ${
                    t.featured
                      ? "bg-[#41C2F2] text-[#0C0A09] hover:bg-[#BDE9FA]"
                      : "border border-stone-600 text-stone-200 hover:border-[#41C2F2] hover:text-[#41C2F2]"
                  }`}
                >
                  Commission this size
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
